import { ElementThemeColorValueTypes, ThemeMode, ThemeColorScaleTypes } from '../enums/index.mjs';
import generateColorTheme, { generateColorV2Theme } from './color/index.mjs';
import fontWeights from './constants/font.mjs';
import zIndexSet from './constants/zIndex.mjs';
import elementTheme2ColorSet, { getFogColorSetGenerator } from './helpers/elementTheme2ColorSet.mjs';
import { hex2RGBA } from './helpers/hexToRgba.mjs';
import generateShadowSet from './shadow/index.mjs';
export { MoimThemeProvider } from './MoimThemeProvider.mjs';

var elementThemePaletteGenerator = function elementThemePaletteGenerator(palette, element) {
  return {
    topArea: {
      background: elementTheme2ColorSet(palette, element.topArea.background),
      moimNameText: elementTheme2ColorSet(palette, element.topArea.moimNameText),
      menuText: elementTheme2ColorSet(palette, element.topArea.menuText),
      others: elementTheme2ColorSet(palette, element.topArea.others)
    },
    topSubArea: {
      background: elementTheme2ColorSet(palette, element.topSubArea.background),
      childMoimNameText: elementTheme2ColorSet(palette, element.topSubArea.childMoimNameText),
      menuText: elementTheme2ColorSet(palette, element.topSubArea.menuText)
    },
    sideArea: {
      background: elementTheme2ColorSet(palette, element.sideArea.background),
      childMoimNameText: elementTheme2ColorSet(palette, element.sideArea.childMoimNameText),
      categoryTitleText: elementTheme2ColorSet(palette, element.sideArea.categoryTitleText),
      menuText: elementTheme2ColorSet(palette, element.sideArea.menuText),
      others: elementTheme2ColorSet(palette, element.sideArea.others)
    },
    alert: {
      alertBadge: elementTheme2ColorSet(palette, element.alert.alertBadge)
    },
    buttons: {
      button: elementTheme2ColorSet(palette, element.buttons.button)
    },
    reactionButtons: {
      like: elementTheme2ColorSet(palette, element.reactionButtons.like),
      dislike: elementTheme2ColorSet(palette, element.reactionButtons.dislike)
    }
  };
};
function makeTheme(data) {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  var themeMode = data.themeMode,
    setThemeMode = data.setThemeMode,
    locale = data.locale,
    palette = data.palette,
    darkPalette = data.darkPalette,
    elementThemePalette = data.elementThemePalette,
    systemColor = data.systemColor,
    darkSystemColor = data.darkSystemColor;
  var colorSet = generateColorTheme(themeMode === ThemeMode.DARK ? darkSystemColor : systemColor);
  var originalPalette = palette;
  var themePalette = themeMode === ThemeMode.DARK ? darkPalette : palette;
  var originalColorV2Set = generateColorV2Theme(originalPalette);
  var themeColorV2Set = generateColorV2Theme(themePalette);
  var getTopAreaElementPalette = function getTopAreaElementPalette(element) {
    var _a;
    return element ? (_a = elementThemePalette.topArea[element]) !== null && _a !== void 0 ? _a : originalColorV2Set.colorSetV2 : originalColorV2Set.colorSetV2;
  };
  var getTopSubAreaElementPalette = function getTopSubAreaElementPalette(element) {
    var _a;
    return element ? (_a = elementThemePalette.topSubArea[element]) !== null && _a !== void 0 ? _a : originalColorV2Set.colorSetV2 : originalColorV2Set.colorSetV2;
  };
  var getSideAreaElementPalette = function getSideAreaElementPalette(element) {
    var _a;
    return element ? (_a = elementThemePalette.sideArea[element]) !== null && _a !== void 0 ? _a : originalColorV2Set.colorSetV2 : originalColorV2Set.colorSetV2;
  };
  var getAlertElementPalette = function getAlertElementPalette(element) {
    var _a;
    return element ? (_a = elementThemePalette.alert[element]) !== null && _a !== void 0 ? _a : originalColorV2Set.colorSetV2 : originalColorV2Set.colorSetV2;
  };
  var getButtonElementPalette = function getButtonElementPalette(element) {
    var _a;
    return element ? (_a = elementThemePalette.buttons[element]) !== null && _a !== void 0 ? _a : originalColorV2Set.colorSetV2 : originalColorV2Set.colorSetV2;
  };
  var getReactionButtonElementPalette = function getReactionButtonElementPalette(element) {
    var _a;
    return element ? (_a = elementThemePalette.reactionButtons[element]) !== null && _a !== void 0 ? _a : originalColorV2Set.colorSetV2 : originalColorV2Set.colorSetV2;
  };
  var getThemeElementColor = function getThemeElementColor(_ref) {
    var targetColor = _ref.targetColor,
      elementPalette = _ref.elementPalette,
      fallback = _ref.fallback;
    var _a, _b, _c, _d, _e, _f;
    switch (elementPalette === null || elementPalette === void 0 ? void 0 : elementPalette.type) {
      case "topArea":
        {
          return (_a = getTopAreaElementPalette(elementPalette.key)[targetColor]) !== null && _a !== void 0 ? _a : fallback;
        }
      case "topSubArea":
        {
          return (_b = getTopSubAreaElementPalette(elementPalette.key)[targetColor]) !== null && _b !== void 0 ? _b : fallback;
        }
      case "sideArea":
        {
          return (_c = getSideAreaElementPalette(elementPalette.key)[targetColor]) !== null && _c !== void 0 ? _c : fallback;
        }
      case "alert":
        {
          return (_d = getAlertElementPalette(elementPalette.key)[targetColor]) !== null && _d !== void 0 ? _d : fallback;
        }
      case "buttons":
        {
          return (_e = getButtonElementPalette(elementPalette.key)[targetColor]) !== null && _e !== void 0 ? _e : fallback;
        }
      case "reactionButtons":
        {
          return (_f = getReactionButtonElementPalette(elementPalette.key)[targetColor]) !== null && _f !== void 0 ? _f : fallback;
        }
      default:
        return fallback;
    }
  };
  var getPrimaryFogPalette = getFogColorSetGenerator(originalPalette.colorSetV2, originalPalette.primaryFogType, originalPalette.primary.main);
  var getSecondaryFogPalette = getFogColorSetGenerator(originalPalette.colorSetV2, originalPalette.primaryFogType, originalPalette.primary.main);
  var getColorByAlias = getColorByAliasGenerator(originalColorV2Set);
  var touchedLightPalette = Object.assign(Object.assign({}, palette), {
    colorSet: palette.colorSetV2
  });
  var touchedDarkPalette = Object.assign(Object.assign({}, darkPalette), {
    colorSet: darkPalette.colorSetV2
  });
  return {
    locale: locale,
    elementThemePalette: elementThemePalette,
    font: fontWeights,
    zIndexes: zIndexSet,
    color: colorSet,
    shadow: generateShadowSet(themeColorV2Set.colorSetV2),
    colorV2: Object.assign(Object.assign({}, themeColorV2Set), {
      colorSet: themeColorV2Set.colorSetV2
    }),
    themeMode: {
      mode: themeMode,
      lightPalette: touchedLightPalette,
      darkPalette: touchedDarkPalette
    },
    getTopAreaElementPalette: getTopAreaElementPalette,
    getTopSubAreaElementPalette: getTopSubAreaElementPalette,
    getSideAreaElementPalette: getSideAreaElementPalette,
    getAlertElementPalette: getAlertElementPalette,
    getButtonElementPalette: getButtonElementPalette,
    getReactionButtonElementPalette: getReactionButtonElementPalette,
    getPrimaryFogPalette: getPrimaryFogPalette,
    getThemeElementColor: getThemeElementColor,
    getSecondaryFogPalette: getSecondaryFogPalette,
    getColorByAlias: getColorByAlias,
    setThemeMode: setThemeMode,
    getBlockitColorValue: createBlockitColorValue(touchedLightPalette, touchedDarkPalette, getColorByAlias)
  };
}
function getColorByAlias(colorData, alias, fallback) {
  switch (alias === null || alias === void 0 ? void 0 : alias.toUpperCase()) {
    case ElementThemeColorValueTypes.PRIMARY_MAIN:
      {
        return colorData.primary.main;
      }
    case ElementThemeColorValueTypes.PRIMARY_LIGHT:
      {
        return colorData.primary.light;
      }
    case ElementThemeColorValueTypes.PRIMARY_DARK:
      {
        return colorData.primary.dark;
      }
    case ElementThemeColorValueTypes.SECONDARY_MAIN:
      {
        return colorData.secondary.main;
      }
    case ElementThemeColorValueTypes.SECONDARY_LIGHT:
      {
        return colorData.secondary.light;
      }
    case ElementThemeColorValueTypes.SECONDARY_DARK:
      {
        return colorData.secondary.dark;
      }
    case ElementThemeColorValueTypes.BLACK:
      {
        return "rgba(0,0,0,1.00)";
      }
    case ElementThemeColorValueTypes.WHITE:
      {
        return "rgba(255,255,255,1.00)";
      }
    default:
      {
        if (alias && typeof alias === "string" && alias.startsWith("#")) {
          if (alias.length >= 8) {
            return hex2RGBA(alias);
          }
          return alias;
        } else if (typeof alias === "string" && alias.startsWith("rgb")) {
          return alias;
        }
        return fallback;
      }
  }
}
function getColorByAliasGenerator(theme) {
  return function (alias, fallback) {
    return getColorByAlias(theme, alias, fallback);
  };
}
function createBlockitColorValue(lightPalette, darkPalette, _getColorByAlias) {
  return function (alias, referencePaletteKey) {
    if (referencePaletteKey) {
      if (alias === ThemeColorScaleTypes.BLACK) {
        return lightPalette.colorSet[referencePaletteKey];
      }
      if (alias === ThemeColorScaleTypes.WHITE) {
        return darkPalette.colorSet[referencePaletteKey];
      }
    }
    return _getColorByAlias(alias);
  };
}

export { elementThemePaletteGenerator, getColorByAlias, getColorByAliasGenerator, makeTheme };
